import React, { useState } from 'react';
import {
    Card,
    Button,
    Space,
    Typography,
    Input,
    DatePicker,
    Select,
    Form
} from 'antd';
import {
    ReloadOutlined,
    FilterOutlined,
    SearchOutlined
} from '@ant-design/icons';
import RequestList from '../components/requests/RequestList';
import { useQuery } from '@tanstack/react-query';
import { useGetMyRequests } from '../../api';
import dayjs from 'dayjs';
import type { ActionFlowRequestDTO } from '../../api';

const { Title } = Typography;
const { RangePicker } = DatePicker;

interface SearchState {
    numberOfRows?: number;
    sortBy?: string;
    orderBy?: string;
    q?: string;
    domain?: string;
    from?: string;
    to?: string;
}

export const MyPendingRequestsView: React.FC = () => {
    const [form] = Form.useForm();
    const [searchParams, setSearchParams] = useState<SearchState>({
        numberOfRows: 40,
        sortBy: 'scopeDate',
        orderBy: 'desc'
    });

    const { data, isLoading, error, refetch } = useGetMyRequests(
        { search: { ...searchParams, status: 'PENDING' as any } },
        { query: { enabled: true } }
    );

    const requests: ActionFlowRequestDTO[] = Array.isArray(data) ? data : [];

    const handleSearch = (values: any) => {
        const { dateRange, ...rest } = values;
        setSearchParams((prev) => ({
            ...prev,
            ...rest,
            from: dateRange ? dayjs(dateRange[0]).startOf('day').format('YYYY-MM-DD') : undefined,
            to: dateRange ? dayjs(dateRange[1]).endOf('day').format('YYYY-MM-DD') : undefined
        }));
    };

    const handleReset = () => {
        form.resetFields();
        setSearchParams({ numberOfRows: 40, sortBy: 'scopeDate', orderBy: 'desc' });
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <Title level={2}>My Pending Requests</Title>
                <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
                    Refresh
                </Button>
            </div>

            <Card>
                <Form form={form} layout="vertical" onFinish={handleSearch} className="mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Form.Item name="q" label="Search">
                            <Input prefix={<SearchOutlined />} placeholder="Search records..." allowClear />
                        </Form.Item>
                        <Form.Item name="domain" label="Domain">
                            <Select
                                placeholder="Select domain"
                                allowClear
                                options={[
                                    { label: 'Loans', value: 'LOANS' },
                                    { label: 'Accounts', value: 'ACCOUNTS' },
                                    { label: 'Customers', value: 'CUSTOMERS' }
                                ]}
                            />
                        </Form.Item>
                        <Form.Item name="dateRange" label="Date Range">
                            <RangePicker style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item label=" " className="flex justify-end">
                            <Space>
                                <Button onClick={handleReset}>Reset</Button>
                                <Button type="primary" htmlType="submit" icon={<FilterOutlined />}>
                                    Filter
                                </Button>
                            </Space>
                        </Form.Item>
                    </div>
                </Form>

                <RequestList
                    requests={requests}
                    isLoading={isLoading}
                    error={error as Error | null}
                    showResendAction={false}
                />
            </Card>
        </div>
    );
};

export default MyPendingRequestsView;
